import { ConvenienceFeeDto } from 'apps/public-portal/src/app/core/api/PaymentIntegrationApiClients';

/**
 * Identifies one cart partition by (Teller system, MID-override). Round-tripped
 * from prepare to the re-tokenize endpoint so the per-partition state survives
 * the hop without a second Prepare probe (Phase 5).
 */
export class CartPartitionRef {
    tellerSystemCode: string;

    paymentProcessorConfigKey: string;

    constructor(data?: Partial<CartPartitionRef>) {
        if (data) {
            this.tellerSystemCode = data.tellerSystemCode;
            this.paymentProcessorConfigKey = data.paymentProcessorConfigKey;
        }
    }
}

/**
 * One partition's session token minted during Prepare. Round-tripped from prepare
 * → frontend → payCart so the per-partition session handle is available when
 * MakePayment fans Authorize / Capture / Cancel calls back out per partition
 * (FIS-style propagation, but per-partition because Generic carts can span
 * multiple plugins).
 */
export class CartPartitionSessionToken {
    tellerSystemCode: string;

    paymentProcessorConfigKey: string;

    sessionTokenData: string;

    constructor(data?: Partial<CartPartitionSessionToken>) {
        if (data) {
            this.tellerSystemCode = data.tellerSystemCode;
            this.paymentProcessorConfigKey = data.paymentProcessorConfigKey;
            this.sessionTokenData = data.sessionTokenData;
        }
    }
}

export class IFramePaymentPrepareResponse {
    paymentToken: string;

    convenienceFee: number;

    processorPaymentMethodType: string;

    /**
     * Cart partitions whose plugin reported `canProcessSavedPaymentMethod=false`
     * for the saved method's wallet id. Empty when every partition can authorize
     * directly. The frontend round-trips this verbatim to the re-tokenize
     * endpoint when minting the missing token rows so the endpoint doesn't
     * re-probe Prepare for the same per-partition info (Phase 5). Optional so
     * non-Generic integrators that don't know about the flag don't have to
     * populate it.
     */
    partitionsNeedingReTokenize?: CartPartitionRef[];

    /**
     * Per-partition session handles minted during Prepare. The frontend round-
     * trips this verbatim on the subsequent payCart payload so the backend's
     * MakePayment can echo each partition's handle on its Authorize / Capture /
     * CancelAuthorization wire call. Optional / empty for processors that don't
     * initialize per-payment sessions.
     */
    partitionSessionTokens?: CartPartitionSessionToken[];

    /**
     * Per-partition convenience fees minted during Prepare. Reuses the wire-level
     * `ConvenienceFeeDto` shape (`{amount, configKey, tellerSystemCode}`) so the
     * frontend can feed them straight into `cartService.calculateTotals` via a
     * hand-rolled resolved callback — same path as the `/convenience-fee/...`
     * API response. Optional / empty for processors that don't return per-
     * partition fees on Prepare.
     */
    partitionConvenienceFees?: ConvenienceFeeDto[];

    constructor(data?: IFramePaymentPrepareResponse) {
        if (data) {
            for (const property in data) {
                if (Object.prototype.hasOwnProperty.call(data, property)) (<any> this)[property] = (<any>data)[property];
            }
        }
    }
}
